import React from "react";
import { AttachMoney, Sell } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import {
  documentNumberFormatter,
  onlyNumbers,
} from "../../../../services/UtilsService";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addBank } from "../../../../store/associateSlice";
import { BankAccount, Relationship } from "../../../../@types/subscriptionInfo";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../hooks/hooks";
import SubscriptionService from "../../../../services/SubscriptionService";
import { enableCashAdvance } from "../../../../store/profileSlice";
import bankOptions from "./data/bankOptions";
import { BankInput, bankSchema } from "./validations/bankSchema";
import {
  AccountMask,
  AgencyMask,
} from "../../../../components/Mask/DelendTextMask";

interface BusinessBankFormProps {
  handleBackStep(step: string): void;
  close(): void;
}

const BusinessBankForm: React.FC<BusinessBankFormProps> = ({
  handleBackStep,
  close,
}) => {
  const documentNumber = useSelector<RootState>(
    (state) => state.associateSlice.subscriptionInfo?.ownerPerson.documentNumber
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BankInput>({
    resolver: zodResolver(bankSchema),
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const generateRelationships = (relationships: Relationship[]) => {
    return relationships.map((relationship) => {
      return {
        name: relationship.name,
        documentNumber: relationship.documentNumber,
        email: relationship.email,
        mobilePhone: "55" + onlyNumbers(relationship.mobilePhone),
        address: {
          address: relationship.address.address,
          additionalInfo: relationship.address.additionalInfo,
          city: relationship.address.city,
          neighborhood: relationship.address.neighborhood,
          number: relationship.address.number,
          state: relationship.address.state,
          zipCode: relationship.address.zipCode,
          IBGECode: relationship.address.ibgeCode,
        },
        documents: relationship.documents,
        relationshipType: relationship.relationshipType,
      };
    });
  };

  const associateSlice = useAppSelector((selector) => selector.associateSlice);

  const onSubmitHandler: SubmitHandler<BankAccount> = (values) => {
    dispatch(addBank(values));
    const data = {
      ownerPerson: {
        name: associateSlice.subscriptionInfo?.ownerPerson.name,
        documentNumber:
          associateSlice.subscriptionInfo?.ownerPerson.documentNumber,
        address: associateSlice.address,
        bankAccount: values,
        relationships: generateRelationships(associateSlice.relationships),
      },
    };

    const executeUpdate = async () => {
      const response = await SubscriptionService.update(data);

      if (response.status === 200) {
        dispatch(enableCashAdvance());
        navigate("credit", { replace: true });
      }
    };

    return executeUpdate();
  };

  return (
    <>
      <Box
        gap={2}
        display={"flex"}
        sx={{
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          padding: 2,
          background:
            "linear-gradient(229deg, #41B0F8 0%, #626DFD 50.52%, #7232FB 100%)",
        }}
      >
        <div>
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            sx={{
              bgcolor: "#9880FD",
              padding: 0.5,
              borderRadius: "20px",
            }}
          >
            <AttachMoney
              sx={{
                color: "white",
              }}
            />
          </Box>
        </div>

        <Box>
          <Box display={"flex"} gap={0.5}>
            <Typography fontWeight="600" fontSize="14px" color={"white"}>
              04
            </Typography>
            <Typography fontWeight="400" fontSize="14px" color={"white"}>
              de 04
            </Typography>
          </Box>
          <Typography fontWeight="600" fontSize="18px" color={"white"}>
            Dados bancários
          </Typography>
        </Box>
        <Box display="flex" style={{ flex: 1, justifyContent: "flex-end" }}>
          <IconButton>
            <CloseIcon sx={{ color: "#fff" }} onClick={close} />
          </IconButton>
        </Box>
      </Box>

      <Box padding={3} display={"flex"} flexDirection={"column"} gap={2}>
        <Typography fontWeight="400" fontSize="16px" color={"black"}>
          Para finalizar precisamos de algumas informações financeiras.
        </Typography>

        <Box display={"flex"}>
          <Box
            border={1}
            display={"flex"}
            paddingX={2}
            paddingY={0.5}
            borderRadius={6}
            borderColor={"#E9EBF0"}
            gap={1}
          >
            <Sell color="primary" />

            <Typography fontWeight="400" fontSize="16px" color={"black"}>
              CNPJ {documentNumberFormatter(documentNumber)}
            </Typography>
          </Box>
        </Box>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(onSubmitHandler)}
        >
          <TextField
            fullWidth
            select
            label="Escolha o banco"
            variant="standard"
            {...register("code")}
            error={!!errors["code"]}
            helperText={errors["code"] ? errors["code"].message : ""}
            style={{ display: "flex" }}
          >
            {bankOptions.map((bankOption) => {
              return (
                <MenuItem key={bankOption.value} value={bankOption.value}>
                  <Box display={"flex"} alignItems={"center"} mr={1} gap={1}>
                    <img alt="logo" width={"32px"} src={bankOption.imgUrl} />
                    {bankOption.text}
                  </Box>
                </MenuItem>
              );
            })}
          </TextField>

          <Box display={"flex"} sx={{ mt: 3 }} gap={2}>
            <TextField
              label="Agência"
              {...register("agency")}
              error={!!errors["agency"]}
              helperText={errors["agency"] ? errors["agency"].message : ""}
              variant="standard"
              InputProps={{
                inputComponent: AgencyMask as any,
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
            <TextField
              label="Conta Corrente"
              {...register("accountNumber")}
              error={!!errors["accountNumber"]}
              helperText={
                errors["accountNumber"] ? errors["accountNumber"].message : ""
              }
              variant="standard"
              InputProps={{
                inputComponent: AccountMask as any,
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 8,
            }}
          >
            <Button
              onClick={() => {
                handleBackStep("zipCode");
              }}
              variant="outlined"
              color="secondary"
            >
              Voltar
            </Button>

            <Button
              disabled={isSubmitting}
              type="submit"
              variant="contained"
              sx={{ marginLeft: 1 }}
            >
              Finalizar
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default BusinessBankForm;
